/**
 * Which microphone, which speaker, and which of them the phone rings on.
 *
 * React-free, and stored in this browser rather than on the account, because a
 * device id is issued per origin per browser profile: the same headset is a
 * different string on the operator's second laptop, and in another profile it is
 * no string at all. A server-side copy would be a list of identifiers that mean
 * nothing on the machine that read them back.
 *
 * The stored choice is what the operator ASKED for and it is never rewritten by
 * what happens to be plugged in. A headset unplugged over lunch has to be the
 * headset again at two o'clock without anybody choosing it a second time, so the
 * one that vanished stays in storage and only drops out of the EFFECTIVE choice
 * - the same wanted/effective split the per-call recording flags use, and for
 * the same reason: forgetting a preference because the hardware blinked is a
 * setting that silently resets itself.
 */

export interface AudioChoice {
  /** The microphone. `""` means "whatever the system hands over". */
  input: string;
  /** Where the call itself is heard. */
  output: string;
  /**
   * Where an incoming call rings.
   *
   * Its own field rather than a share of `output`, and that is the whole point
   * of the feature: the operator wears a headset, and a call that rings only
   * inside it is a call missed every time they stand up. Ringing the room and
   * talking into the headset is the normal desk arrangement, so it has to be
   * expressible.
   */
  ring: string;
}

/** Nothing chosen: every device follows the system. */
export const AUDIO_CHOICE_NONE: AudioChoice = { input: "", output: "", ring: "" };

const KEY = "crm.call.audio";

/** The three fields, in the order they are drawn and applied. */
export const AUDIO_KINDS = ["input", "output", "ring"] as const;
export type AudioKind = (typeof AUDIO_KINDS)[number];

function isChoice(value: unknown): value is AudioChoice {
  if (!value || typeof value !== "object") return false;
  const raw = value as Record<string, unknown>;
  return AUDIO_KINDS.every((kind) => typeof raw[kind] === "string");
}

/** What this browser was last told. Never throws: storage can be switched off. */
export function readAudioChoice(): AudioChoice {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return AUDIO_CHOICE_NONE;
    const parsed: unknown = JSON.parse(raw);
    return isChoice(parsed) ? { input: parsed.input, output: parsed.output, ring: parsed.ring } : AUDIO_CHOICE_NONE;
  } catch {
    // Private mode, storage off, or a value written by an older shape. Nothing
    // was ever chosen here, which is a working answer rather than a failure.
    return AUDIO_CHOICE_NONE;
  }
}

export function writeAudioChoice(next: AudioChoice): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    // Not being able to remember the choice is no reason to refuse it: the
    // pickers still apply it to the running device for this session.
  }
}

/**
 * What can be applied RIGHT NOW, given the devices actually attached.
 *
 * A chosen id that is not in the list is dropped to `""` here and left alone in
 * storage. Twilio rejects `speakerDevices.set()` for an id it cannot find, so
 * sending one through would turn a headset in a drawer into a failed promise on
 * every call; falling back to the system default is what the operator would get
 * from the browser anyway.
 */
export function effectiveAudioChoice(wanted: AudioChoice, attached: ReadonlySet<string>): AudioChoice {
  return {
    input: attached.has(wanted.input) ? wanted.input : "",
    output: attached.has(wanted.output) ? wanted.output : "",
    ring: attached.has(wanted.ring) ? wanted.ring : "",
  };
}

/**
 * The kinds whose chosen device is not plugged in at the moment.
 *
 * Drawn as a note beside the list rather than silently corrected. "The call is
 * going somewhere other than where you chose" is exactly the state this feature
 * exists to make visible, and a picker that quietly showed "System default"
 * would be hiding it again.
 */
export function detachedAudioKinds(
  wanted: AudioChoice,
  attached: ReadonlySet<string>,
  /**
   * The browser has answered `enumerateDevices`. Until it has, `attached` is
   * empty because nothing has been counted yet - NOT because the desk is bare,
   * and the difference is the whole reason this argument exists. The panel
   * mounts at the instant its dropdown opens, so for the first frames every
   * stored device looked unplugged: three warning notes, two lines each,
   * appearing and then withdrawn a moment later. That is the jump an operator
   * sees, and it lands on top of the entrance animation.
   */
  known = true,
): AudioKind[] {
  if (!known) return [];
  return AUDIO_KINDS.filter((kind) => wanted[kind] !== "" && !attached.has(wanted[kind]));
}

/**
 * The entries a list may actually offer.
 *
 * A browser that has not been granted the microphone is allowed to answer
 * `enumerateDevices` with ONE placeholder per kind, carrying an empty id and an
 * empty label - and Chrome does. Drawn as a row it is worse than useless: it
 * cannot be applied (Twilio refuses an empty id) and, because "" is also how
 * this app spells "no choice", it lit the check mark on both itself and the
 * System default row above it. Every list showed two selected entries at once.
 */
export function selectableDevices<T extends { deviceId: string }>(list: readonly T[]): T[] {
  return list.filter((device) => device.deviceId !== "");
}

/**
 * A device's name, or something usable when the browser will not give one.
 *
 * `enumerateDevices` returns entries with an EMPTY label until the page has been
 * granted the microphone once - the ids are there, the names are not. Numbering
 * them is not a nicety in that state: it is the difference between a list of
 * blanks and a list somebody can at least count through.
 */
export function audioDeviceLabel(
  device: Pick<MediaDeviceInfo, "label" | "deviceId">,
  index: number,
  unnamed: (n: number) => string,
): string {
  const label = device.label.trim();
  return label || unnamed(index + 1);
}

/** The four things a collapsed picker row can say about its own choice. */
export type ChosenState = "pending" | "default" | "device" | "gone";

/**
 * What a folded combobox row shows: the chosen device by name, or why it cannot.
 *
 * The list itself is only unfolded when somebody is changing the device, so this
 * one line is what the panel says the rest of the time - which makes "gone" a
 * state it has to be able to say out loud rather than a silent fallback to the
 * system default. Same rule as `detachedAudioKinds`, one line higher up.
 *
 * "Not yet asked" and "asked, and it is not there" are two different answers.
 * An empty list means the second only once the browser has spoken; before that
 * it means nothing at all, and a row that reads "Not connected" for four frames
 * and then names the headset is a row that cried wolf. `known` is what keeps
 * them apart - and unlike the note below it, this state still has to say
 * SOMETHING, because a row whose value line went empty would be a row half a
 * line shorter, which is the panel changing height as it opens.
 */
export function chosenDeviceLabel<T extends Pick<MediaDeviceInfo, "label" | "deviceId">>(
  chosen: string,
  list: readonly T[],
  names: { systemDefault: string; gone: string; pending: string; unnamed: (n: number) => string },
  known = true,
): { state: ChosenState; label: string } {
  // Ahead of the `known` check on purpose: an unset field asks for the system
  // default, which is true before anything has been enumerated and after.
  if (chosen === "") return { state: "default", label: names.systemDefault };
  if (!known) return { state: "pending", label: names.pending };
  const index = list.findIndex((device) => device.deviceId === chosen);
  const device = index < 0 ? undefined : list[index];
  if (!device) return { state: "gone", label: names.gone };
  return { state: "device", label: audioDeviceLabel(device, index, names.unnamed) };
}
